import { useCallback, useEffect, useRef, useState } from "react";
import { useAnalytics } from "~/lib/firebase/analytics";

interface DynamicEleType {
  id: number;
  url: {
    small: string;
    medium: string;
    large: string;
    original: string;
  };
  subHtml?: string;
  photoName: string;
}

type LightGalleryWrapper = {
  images: DynamicEleType[];
  indexToOpen: number | null;
  setLightBoxIndex?: (index: number | null) => void;
};

export default function LightGalleryWrapper({ images, indexToOpen, setLightBoxIndex }: LightGalleryWrapper) {
  const lightGallery = useRef<any>(null);
  const [LightGallery, setLightGallery] = useState<any>(null);
  const [plugins, setPlugins] = useState<any[]>([]);
  const { trackDownload } = useAnalytics();

  useEffect(() => {
    // Dynamically import lightgallery/react
    import("lightgallery/react").then((mod) => {
      setLightGallery(() => mod.default);
    });
  }, []);

  const onInit = useCallback((detail: any) => {
    if (detail) {
      lightGallery.current = detail.instance;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Dynamically load plugins and CSS only in the browser
    Promise.all([
      import("lightgallery/plugins/thumbnail"),
      import("lightgallery/plugins/zoom"),
      import("lightgallery/plugins/fullscreen"),
      import("lightgallery/plugins/autoplay"),
      import("lightgallery/css/lg-thumbnail.css"),
      import("lightgallery/css/lg-fullscreen.css"),
      import("lightgallery/css/lg-zoom.css"),
      import("lightgallery/css/lg-autoplay.css"),
      import("lightgallery/css/lightgallery.css"),
    ]).then(([thumb, zoom, fullscreen, autoplay]) => {
      if (mounted) {
        setPlugins([thumb.default, zoom.default, fullscreen.default, autoplay.default]);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Custom download function
  const handleDownload = useCallback(async (url: string, filename?: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Extract extension from URL
      const urlPath = url.split("?")[0]; // Remove query parameters
      const urlExtension = urlPath.substring(urlPath.lastIndexOf(".")).toLowerCase();
      const validExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
      const extension = validExtensions.includes(urlExtension) ? urlExtension : ".jpg";

      // Ensure filename has extension
      let finalFilename = filename || `image-${Date.now()}`;

      // Check if filename already has an extension
      const hasExtension = validExtensions.some((ext) => finalFilename.toLowerCase().endsWith(ext));
      if (!hasExtension) {
        finalFilename = `${finalFilename}${extension}`;
      }

      const link = document.createElement("a");
      console.log("link: ", link);
      link.href = downloadUrl;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      window.URL.revokeObjectURL(downloadUrl);
      trackDownload(finalFilename, "image", "LightGallery");
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: open in new tab
      window.open(url, "_blank");
    }
  }, []);

  // Add custom styles for transparent blur background and orange icons
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
     /* Prevent pull-to-refresh and unwanted gestures */
     .lg-backdrop,
     .lg-outer,
     .lg-container,
     .custom-gallery-class {
       touch-action: manipulation !important;
       -webkit-user-select: none !important;
       user-select: none !important;
       -webkit-touch-callout: none !important;
     }

     .lg-toolbar {
        background: rgba(0, 0, 0, 0.1) !important;
        backdrop-filter: blur(5px) !important;
        padding-top: 10px !important;
        -webkit-backdrop-filter: blur(5px) !important;
      }

      /* Orange icons with white circle background */
      .lg-icon {
        background-color: rgba(255, 255, 255, 0.15) !important;
        border-radius: 100% !important;
        color: white !important;
        width: 35px !important;
        height: 35px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.3s ease !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
      }

      .lg-icon:hover {
        background-color: rgba(255, 255, 255, 0.25) !important;
        border: 1px solid rgba(255, 255, 255, 0.4) !important;
      }

      /* Specific icon styling */
      .lg-close,
      .lg-download,
      .lg-fullscreen,
      .lg-zoom-in,
      .lg-zoom-out,
      .lg-autoplay-button,
      .lg-prev,
      .lg-next {
        background-color: rgba(255, 255, 255, 0.15) !important;
        background-opacity: 0.15 !important;
        border-radius: 50% !important;
        color: white !important;
        width: 35px !important;
        height: 35px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.3s ease !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
      }

      .lg-close:hover,
      .lg-download:hover,
      .lg-fullscreen:hover,
      .lg-zoom-in:hover,
      .lg-zoom-out:hover,
      .lg-autoplay-button:hover,
      .lg-prev:hover,
      .lg-next:hover {
        background-color: rgba(255, 255, 255, 0.25) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
        border: 1px solid rgba(255, 255, 255, 0.4) !important;
      }

      /* Navigation arrows styling */
      .lg-prev,
      .lg-next {
        width: 35px !important;
        height: 35px !important;
        margin: 10px !important;
      }

      /* Default prev/next SVG icons */
      .lg-prev svg,
      .lg-next svg {
        fill: rgba(255, 255, 255, 0.8) !important;
        color: rgba(255, 255, 255, 0.8) !important;
      }

      /* Toolbar icons */
      .lg-toolbar .lg-icon {
        margin: 0 5px !important;
      }

      /* Autoplay button specific styling */
      .lg-autoplay-button {
        position: relative !important;
      }

      /* Download button */
      .lg-download {
        margin-right: 10px !important;
      }

      /* Fullscreen button */
      .lg-fullscreen {
        margin-right: 10px !important;
      }

      /* Zoom buttons */
      .lg-zoom-in,
      .lg-zoom-out {
        margin: 0 5px !important;
      }

      /* SVG icons inside buttons */
      .lg-icon svg,
      .lg-close svg,
      .lg-download svg,
      .lg-fullscreen svg,
      .lg-zoom-in svg,
      .lg-zoom-out svg,
      .lg-autoplay-button svg,
      .lg-prev svg,
      .lg-next svg {
        fill: rgba(255, 255, 255, 0.8) !important;
        color: rgba(255, 255, 255, 0.8) !important;
        width: 20px !important;
        height: 20px !important;
      }

      /* For font icons if SVG is not used */
      .lg-icon::before,
      .lg-close::before,
      .lg-download::before,
      .lg-fullscreen::before,
      .lg-zoom-in::before,
      .lg-zoom-out::before,
      .lg-autoplay-button::before,
      .lg-prev::before,
      .lg-next::before {
        color: rgba(255, 255, 255, 0.8) !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (indexToOpen !== null && lightGallery.current) {
      lightGallery.current.openGallery(indexToOpen);
    }
  }, [indexToOpen]);

  // Prevent page refresh on swipe down (Android)
  useEffect(() => {
    const preventTouchRefresh = (e: TouchEvent) => {
      // Prevent default pull-to-refresh behavior
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        const target = e.target as HTMLElement;

        // Only prevent if we're inside the gallery
        if (target.closest(".lg-outer") || target.closest(".custom-gallery-class")) {
          // Allow vertical scroll, but not the overscroll effect
          const scrollTop = (target.closest(".lg-img-wrap") as HTMLElement)?.scrollTop || 0;
          if (scrollTop === 0) {
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("touchmove", preventTouchRefresh, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventTouchRefresh);
    };
  }, []);

  const handleCloseGallery = () => {
    if (setLightBoxIndex) {
      setLightBoxIndex(null);
    }
  };

  // Handle download button click with more robust approach
  const setupDownloadHandler = useCallback(() => {
    const setupHandler = () => {
      const downloadBtn = document.querySelector(".lg-download");
      if (downloadBtn) {
        // Remove existing event listeners by cloning the node
        const newDownloadBtn = downloadBtn.cloneNode(true) as HTMLElement;
        downloadBtn.parentNode?.replaceChild(newDownloadBtn, downloadBtn);

        // Add our custom click handler
        newDownloadBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Get current slide index
          const currentSlide = lightGallery.current?.index || 0;
          const currentImage = images[currentSlide];

          if (currentImage) {
            handleDownload(currentImage.url.original, currentImage?.photoName);
          }
        });
      }
    };

    // Setup handler with multiple attempts to ensure it works
    setTimeout(setupHandler, 100);
    setTimeout(setupHandler, 300);
    setTimeout(setupHandler, 500);
  }, [images, handleDownload]);

  const onAfterSlide = useCallback(() => {
    setupDownloadHandler();
  }, [setupDownloadHandler]);

  const onAfterOpen = useCallback(() => {
    setupDownloadHandler();
  }, [setupDownloadHandler]);

  // Prevent browser back button when gallery is open
  // useEffect(() => {
  //   if (indexToOpen === null) return;

  //   const handlePopState = () => {
  //     if (lightGallery.current) {
  //       lightGallery.current.closeGallery();
  //     }
  //   };

  //   // Push one history state when gallery opens
  //   // window.history.pushState({ gallery: true }, "");

  //   window.addEventListener("popstate", handlePopState);

  //   return () => {
  //     window.removeEventListener("popstate", handlePopState);
  //   };
  // }, [indexToOpen]);

  if (!LightGallery) return null; // or a fallback UI

  if (images?.length === 0) return null;

  return (
    <div
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        touchAction: "manipulation",
      }}
      className="gallery-wrapper"
    >
      <LightGallery
        onInit={onInit}
        elementClassNames="custom-gallery-class"
        zoom={true}
        thumbnail={true}
        fullScreen={true}
        autoplay={true}
        download={true}
        autoplayControls={true}
        mobileSettings={{
          controls: true,
          showCloseIcon: true,
          download: true,
          rotate: false,
        }}
        // thumbWidth={200}
        thumbHeight={"100px"}
        dynamic={true}
        dynamicEl={images?.map((img) => ({
          src: img.url.large,
          thumb: img.url.small,
          downloadUrl: img.url.original,

          alt: img.photoName || "Gallery image",
        }))}
        plugins={plugins}
        onAfterClose={handleCloseGallery}
        onAfterSlide={onAfterSlide}
        onAfterOpen={onAfterOpen}
      />
    </div>
  );
}
